<script setup>
import { computed, nextTick, onMounted, onUnmounted, ref, watch } from "vue";
import { useRoute } from "vue-router";
import { useLangStore } from "@/stores/lang.js";
import { useThemeStore } from "@/stores/theme.js";
import { OFFICIAL_TG_URL, OFFICIAL_X_URL } from "@/lib/officialChannels.js";
import "@/assets/navigation-clarity.css";

const lang = useLangStore();
const theme = useThemeStore();
const route = useRoute();

const onMonitor = computed(() => route.name === "monitor");
const onMintLive = computed(() => route.name === "mint-live");
const onMarket = computed(() => route.name === "market");
const onTasks = computed(() => route.name === "tasks");
const onGuide = computed(() => route.name === "usage-guide");
const onTools = computed(() => onTasks.value || onMarket.value || onMintLive.value || onMonitor.value || onGuide.value);
/** 创建 / 详情（不含独立 Mint 监听看板） */
const onMint = computed(
  () => route.name === "mint-create" || route.name === "mint-detail"
);
const onShop = computed(() => route.name === "shop");
const onDeploy = computed(() => route.name === "deploy-stock" || route.name === "deploy-bbk-v3");
const deployPath = computed(() => onDeploy.value ? route.path : "/deploy/percent-buyback");
const toolsMenu = ref(null);
const toolsOpen = ref(false);
const toolsLabel = computed(() => onMonitor.value ? lang.t("工厂监听", "Factory monitor")
  : onMintLive.value ? lang.t("Mint 监听", "Mint live")
  : onMarket.value ? lang.t("市值工具", "Market tools")
  : onTasks.value ? lang.t("任务中心", "Task center")
  : onGuide.value ? lang.t("使用指南", "Usage guide") : lang.t("工具与指南", "Tools & guides"));

function closeTools(returnFocus = false) {
  if (!toolsMenu.value?.open) return;
  toolsMenu.value.open = false;
  toolsOpen.value = false;
  if (returnFocus) toolsMenu.value.querySelector("summary")?.focus();
}

function onOutsideClick(event) {
  if (!toolsMenu.value?.contains(event.target)) closeTools();
}
async function enterTools(event) {
  if (!["ArrowDown", "ArrowUp"].includes(event.key)) return;
  event.preventDefault();
  toolsMenu.value.open = true;
  toolsOpen.value = true;
  await nextTick();
  const links = toolsMenu.value.querySelectorAll(".nav-tools-menu a");
  links[event.key === "ArrowUp" ? links.length - 1 : 0]?.focus();
}
function moveToolFocus(event) {
  if (!["ArrowDown", "ArrowUp", "Home", "End"].includes(event.key)) return;
  const links = [...toolsMenu.value.querySelectorAll(".nav-tools-menu a")];
  const current = links.indexOf(document.activeElement);
  if (current < 0) return;
  event.preventDefault();
  const target = event.key === "Home" ? 0 : event.key === "End" ? links.length - 1
    : (current + (event.key === "ArrowDown" ? 1 : -1) + links.length) % links.length;
  links[target]?.focus();
}
function onToolsFocusOut(event) {
  // During pointer focus transfer activeElement may temporarily be body.
  // Closing here would hide the link before its click can reach the router.
  if (event.relatedTarget && !toolsMenu.value?.contains(event.relatedTarget)) closeTools();
}

watch(() => route.fullPath, () => closeTools(true));
onMounted(() => document.addEventListener("pointerdown", onOutsideClick));
onUnmounted(() => document.removeEventListener("pointerdown", onOutsideClick));
</script>

<template>
  <header class="topbar is-vault-nav is-navigation-clear">
    <div class="topbar-inner">
      <RouterLink class="logo" to="/" aria-current="false">
        <img
          class="logo-img"
          src="/fengxin-logo.png"
          :alt="lang.t('封心', 'Fengxin')"
          width="32"
          height="32"
        />
        <span class="logo-stack">
          <span class="logo-text">{{ lang.t("封心", "Fengxin") }}</span>
          <span class="logo-desk">FENGXIN</span>
        </span>
      </RouterLink>

      <nav class="nav-primary" :aria-label="lang.t('主导航', 'Main')">
        <RouterLink to="/" :aria-current="route.name === 'home' ? 'page' : 'false'">{{ lang.t("首页", "Home") }}</RouterLink>
        <RouterLink to="/shop" :aria-current="onShop ? 'page' : 'false'">{{
          lang.t("金库市场", "Vault market")
        }}</RouterLink>
        <RouterLink :to="deployPath" :aria-current="onDeploy ? 'page' : 'false'">{{ lang.t("部署", "Deploy") }}</RouterLink>
        <RouterLink
          to="/mint"
          :aria-current="onMint ? 'page' : 'false'"
        >{{ lang.t("公平 Mint", "Fair Mint") }}</RouterLink>
      </nav>

      <div class="topbar-actions">
        <details
          ref="toolsMenu"
          class="nav-tools"
          :class="{ 'is-current': onTools }"
          @keydown.esc.prevent="closeTools(true)"
          @toggle="toolsOpen = $event.target.open"
          @focusout="onToolsFocusOut"
        >
          <summary :aria-current="onTools ? 'true' : 'false'" :aria-expanded="toolsOpen" aria-controls="navigation-tools" @keydown="enterTools">{{ toolsLabel }}<svg class="nav-tools-chevron" viewBox="0 0 20 20" fill="none" stroke="currentColor" stroke-width="1.8" aria-hidden="true"><path d="m5 7 5 5 5-5" /></svg></summary>
          <nav id="navigation-tools" class="nav-tools-menu" :aria-label="lang.t('工具、监听与使用指南', 'Tools, monitoring and usage guide')" @click="closeTools(true)" @keydown="moveToolFocus">
            <RouterLink to="/tasks" :aria-current="onTasks ? 'page' : 'false'"><strong>{{ lang.t('任务中心', 'Task center') }}</strong><span>{{ lang.t('进度、交易记录与待核对事项', 'Progress, transactions and pending checks') }}</span></RouterLink>
            <RouterLink
              to="/guide"
              :aria-current="onGuide ? 'page' : 'false'"
            >
              <strong>{{ lang.t("使用指南", "Usage guide") }}</strong>
              <span>{{ lang.t("图解站内使用与 Flap 手动部署", "Illustrated site usage and manual deployment on Flap") }}</span>
            </RouterLink>
            <RouterLink
              to="/market"
              :aria-current="onMarket ? 'page' : 'false'"
            >
              <strong>{{ lang.t("市值工具", "Market tools") }}</strong>
              <span>{{ lang.t("配置交易、批量转账与归集", "Trading, batch transfers and collection") }}</span>
            </RouterLink>
            <RouterLink
              to="/mint/live"
              :aria-current="onMintLive ? 'page' : 'false'"
            >
              <strong>{{ lang.t("Mint 监听", "Mint Live") }}</strong>
              <span>{{ lang.t("查看正在募资的项目与进度", "View fundraising projects and progress") }}</span>
            </RouterLink>
            <RouterLink
              to="/monitor"
              :aria-current="onMonitor ? 'page' : 'false'"
            >
              <strong>{{ lang.t("工厂监听", "Factory monitor") }}</strong>
              <span>{{ lang.t("查看新代币、毕业进度与行情", "New tokens, graduation progress and quotes") }}</span>
            </RouterLink>
          </nav>
        </details>
        <label class="theme-picker" :title="lang.t('界面配色：浅色、雾灰、深色', 'Appearance: Light, Mist, Dark')">
          <svg class="theme-picker-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7" aria-hidden="true">
            <circle cx="12" cy="12" r="8" />
            <path d="M12 4v16a8 8 0 0 0 0-16Z" fill="currentColor" stroke="none" />
          </svg>
          <select :value="theme.mode" :aria-label="lang.t('界面配色', 'Interface appearance')" @change="theme.setMode($event.target.value)">
            <option value="light">{{ lang.t("浅色", "Light") }}</option>
            <option value="mist">{{ lang.t("雾灰", "Mist") }}</option>
            <option value="dark">{{ lang.t("深色", "Dark") }}</option>
          </select>
          <svg class="theme-picker-chevron" viewBox="0 0 20 20" fill="none" stroke="currentColor" stroke-width="1.8" aria-hidden="true"><path d="m5 7 5 5 5-5" /></svg>
        </label>
        <button
          type="button"
          class="lang-toggle"
          :aria-label="lang.t('切换为英文', 'Switch to Chinese')"
          @click="lang.toggle()"
        >
          {{ lang.isEn ? "中文" : "EN" }}
        </button>
        <div class="nav-links">
          <a :href="OFFICIAL_X_URL" target="_blank" rel="noopener noreferrer">X</a>
          <a :href="OFFICIAL_TG_URL" target="_blank" rel="noopener noreferrer">{{
            lang.t("电报", "TG")
          }}</a>
        </div>
      </div>
    </div>
  </header>
</template>
